import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ImageOff, Loader2, Pencil, Plus, Save, Sparkles, Trash2, Wand2, X } from 'lucide-react';
import { api } from '@/lib/api';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { Lightbox } from '@/pages/brain/Lightbox';
import type {
  DirectorLocation,
  DirectorPlan,
  DirectorTaskSnapshot,
  LocationLockRun,
  LocationLockVariant,
  LocationLook,
} from './types';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  episodeId: string;
  location: DirectorLocation;
}

type Resolution = '2K' | '4K';
type Count = 1 | 2 | 3;

const VARIANT_LABEL: Record<LocationLockVariant, string> = {
  establishing: '定场',
};

const VARIANT_DESC: Record<LocationLockVariant, string> = {
  establishing: '定场图是 2×2 四格场地设定集,已包含主视图、超广角、俯视布局和关键元素特写。',
};

export function LocationLockDialog({ open, onOpenChange, episodeId, location: locationProp }: Props) {
  const qc = useQueryClient();
  const basePath = `director/episodes/${episodeId}/director/locations/${locationProp.id}`;

  const planQuery = useQuery<DirectorPlan>({
    queryKey: ['director', 'plan', episodeId],
    queryFn: () => api.get(`director/episodes/${episodeId}/director/plan`).json<DirectorPlan>(),
    enabled: open,
    staleTime: 0,
    refetchInterval: (q) => {
      const data = q.state.data as DirectorPlan | undefined;
      if (!data) return false;
      const loc = data.locations.find((l) => l.id === locationProp.id);
      if (!loc) return false;
      const allRuns = [
        ...(loc.lockRuns ?? []),
        ...(loc.looks ?? []).flatMap((lk) => lk.lockRuns ?? []),
      ];
      const anyRunning = allRuns.some((r) => {
        if (r.status !== 'running' && r.status !== 'queued') return false;
        return !(r.candidates.length > 0 && r.candidates.every((c) => c.url || c.error));
      });
      return anyRunning ? 3000 : false;
    },
  });
  const location = useMemo(
    () => planQuery.data?.locations.find((l) => l.id === locationProp.id) ?? locationProp,
    [planQuery.data, locationProp],
  );

  const [activeLookId, setActiveLookId] = useState<string | null>(null);
  const [variant] = useState<LocationLockVariant>('establishing');
  const [resolution, setResolution] = useState<Resolution>('2K');
  const [count, setCount] = useState<Count>(3);
  const [model, setModel] = useState<'seedream' | 'gpt-image'>('seedream');
  const [pollTaskIds, setPollTaskIds] = useState<Partial<Record<string, string>>>({});
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const [notesEditing, setNotesEditing] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [notesHint, setNotesHint] = useState('');
  const [notesAiPreview, setNotesAiPreview] = useState<string | null>(null);

  const [showNewLookForm, setShowNewLookForm] = useState(false);
  const [newLookDesc, setNewLookDesc] = useState('');

  const [editingLookId, setEditingLookId] = useState<string | null>(null);
  const [editLookDesc, setEditLookDesc] = useState('');

  const [selectedCharRefs, setSelectedCharRefs] = useState<Record<string, string>>({});

  const availableCharRefs = useMemo(() => {
    const chars = planQuery.data?.characters ?? [];
    const refs: { id: string; name: string; url: string }[] = [];
    const seen = new Set<string>();
    for (const c of chars) {
      const cardUrl = c.profileImageUrl;
      if (cardUrl && !seen.has(cardUrl)) {
        seen.add(cardUrl);
        refs.push({ id: c.id, name: c.name, url: cardUrl });
      }
      for (const lk of c.looks ?? []) {
        const lkUrl = lk.profileImageUrl;
        if (lkUrl && !seen.has(lkUrl) && lkUrl !== cardUrl) {
          seen.add(lkUrl);
          refs.push({ id: `${c.id}__${lk.id}`, name: `${c.name} · ${lk.label}`, url: lkUrl });
        }
      }
    }
    return refs;
  }, [planQuery.data]);

  const looks = useMemo<Array<LocationLook & { isBase?: boolean }>>(() => {
    const base = {
      id: '__base__',
      label: '标准形象',
      isBase: true,
      description: null,
      notes: location.notes ?? null,
      lockedImages: location.lockedImages ?? [],
      lockRuns: location.lockRuns ?? [],
    };
    return [base, ...(location.looks ?? [])];
  }, [location]);

  const activeLook = useMemo(
    () => looks.find((l) => l.id === activeLookId) ?? looks[0],
    [looks, activeLookId],
  );

  const lookIdParam = activeLook?.id === '__base__' ? undefined : activeLook?.id;

  useEffect(() => {
    setNotesDraft(activeLook?.notes ?? '');
    setNotesAiPreview(null);
    setNotesEditing(false);
  }, [activeLook?.id, activeLook?.notes]);

  useEffect(() => {
    setSelectedCharRefs({});
  }, [location.id, activeLook?.id]);

  const toggleCharRef = (id: string, url: string) => {
    setSelectedCharRefs((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = url;
      return next;
    });
  };

  const lockedByVariant = useMemo(() => {
    const map: Partial<Record<LocationLockVariant, string>> = {};
    for (const li of activeLook?.lockedImages ?? []) {
      if (li?.variant && li.url) map[li.variant as LocationLockVariant] = li.url;
    }
    return map;
  }, [activeLook?.lockedImages]);

  const runsByVariant = useMemo(
    () =>
      (activeLook?.lockRuns ?? [])
        .filter((r) => r.variant === variant),
    [activeLook?.lockRuns, variant],
  );

  const generateMut = useMutation({
    mutationFn: async () =>
      api
        .post(`${basePath}/lock/generate`, {
          json: {
            variant,
            resolution,
            count,
            model,
            lookId: lookIdParam ?? null,
            characterRefUrls: Object.keys(selectedCharRefs).length > 0
              ? Object.values(selectedCharRefs)
              : null,
          },
        })
        .json<{ taskId: string }>(),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      setPollTaskIds((prev) => ({ ...prev, [`${lookIdParam ?? '__base__'}:${variant}`]: res.taskId }));
      toast.success('开始抽卡', `${count} 张候选 30 秒内陆续返回`);
    },
    onError: (e: Error) => toast.error('抽卡失败', e.message || '请重试'),
  });

  const selectMut = useMutation({
    mutationFn: async ({ runId, candidateId }: { runId: string; candidateId: string }) =>
      api
        .post(`${basePath}/lock/select`, {
          json: { runId, candidateId, variant, lookId: lookIdParam ?? null },
        })
        .json(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      toast.success('已设为该场景的锁定图');
    },
    onError: (e: Error) => toast.error('选定失败', e.message || '请重试'),
  });

  const saveNotesMut = useMutation({
    mutationFn: async () => {
      if (lookIdParam) {
        return api.patch(`${basePath}/looks/${lookIdParam}`, { json: { notes: notesDraft || null } }).json();
      }
      return api.patch(basePath, { json: { notes: notesDraft || null } }).json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      setNotesEditing(false);
      toast.success('补充说明已保存');
    },
    onError: (e: Error) => toast.error('保存失败', e.message || '请重试'),
  });

  const generateNotesMut = useMutation({
    mutationFn: async () =>
      api
        .post(`${basePath}/generate-notes`, { json: { userHint: notesHint.trim() || null } })
        .json<{ notes: string }>(),
    onSuccess: (res) => setNotesAiPreview(res.notes),
    onError: (e: Error) => toast.error('AI 生成失败', e.message || '请重试'),
  });

  const createLookMut = useMutation({
    mutationFn: async () =>
      api
        .post(`${basePath}/looks`, { json: { description: newLookDesc.trim() || null } })
        .json<{ locationId: string; look: LocationLook }>(),
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      setActiveLookId(res.look.id);
      setShowNewLookForm(false);
      setNewLookDesc('');
      toast.success('新形象已创建', res.look.label);
    },
    onError: (e: Error) => toast.error('创建失败', e.message || '请重试'),
  });

  const updateLookMut = useMutation({
    mutationFn: async ({ id, desc }: { id: string; desc: string }) =>
      api
        .patch(`${basePath}/looks/${id}`, { json: { description: desc.trim() || null } })
        .json<{ locationId: string; look: LocationLook }>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      setEditingLookId(null);
      toast.success('形象信息已更新');
    },
    onError: (e: Error) => toast.error('更新失败', e.message || '请重试'),
  });

  const deleteLookMut = useMutation({
    mutationFn: async (id: string) =>
      api.delete(`${basePath}/looks/${id}`).json(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      setActiveLookId(null);
      toast.success('形象已删除');
    },
    onError: (e: Error) => toast.error('删除失败', e.message || '请重试'),
  });

  const deleteRunMut = useMutation({
    mutationFn: async (runId: string) =>
      api.delete(`${basePath}/lock/runs/${runId}`, {
        searchParams: lookIdParam ? { look_id: lookIdParam } : undefined,
      }).json(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      toast.success('抽卡历史已删除');
    },
    onError: (e: Error) => toast.error('删除失败', e.message || '请重试'),
  });

  const pollKey = `${lookIdParam ?? '__base__'}:${variant}`;
  const currentPollTaskId = pollTaskIds[pollKey] ?? null;

  const taskQuery = useQuery({
    queryKey: ['director', 'task', currentPollTaskId],
    queryFn: () => api.get(`director/tasks/${currentPollTaskId}`).json<DirectorTaskSnapshot>(),
    enabled: !!currentPollTaskId,
    refetchInterval: (q) => {
      const s = (q.state.data as DirectorTaskSnapshot | undefined)?.status;
      if (!s) return 4000;
      return s === 'queued' || s === 'running' ? 4000 : false;
    },
  });

  useEffect(() => {
    if (!currentPollTaskId) return;
    const s = taskQuery.data?.status;
    if (s && s !== 'queued' && s !== 'running') {
      setPollTaskIds((prev) => {
        const next = { ...prev };
        delete next[pollKey];
        return next;
      });
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
    }
  }, [taskQuery.data?.status, currentPollTaskId, pollKey, qc, episodeId]);

  const isGenRunning = !!currentPollTaskId || generateMut.isPending;
  const currentLocked = lockedByVariant[variant];
  const sourceNotes = activeLook?.notes ?? '';

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o && lightboxUrl) return; onOpenChange(o); }}
      title={`场地锁定图 · ${location.name}`}
      className="w-[900px] max-w-[95vw] [&>div:last-of-type]:overflow-hidden [&>div:last-of-type]:p-0 [&>div:last-of-type]:mt-0"
      preventCloseOnOutside={!!lightboxUrl}
    >
      <div className="flex h-[70vh] gap-0 overflow-hidden">

        <div className="flex w-44 shrink-0 flex-col border-r border-outline-variant/20 bg-surface-variant/20">
          <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
            {looks.map((lk) => {
              const active = lk.id === activeLook?.id;
              const isBase = lk.id === '__base__';
              const isEditingThis = editingLookId === lk.id;
              const previewUrl = (lk.lockedImages ?? []).find((li) => li.variant === 'establishing')?.url;
              const lockedCount = (lk.lockedImages ?? []).some((li) => li.variant === 'establishing') ? 1 : 0;
              return (
                <div key={lk.id}>
                  <button
                    type="button"
                    onClick={() => { setActiveLookId(lk.id); setEditingLookId(null); }}
                    className={
                      'group relative w-full overflow-hidden rounded-lg border text-left transition ' +
                      (active
                        ? 'border-violet-500 bg-violet-50 ring-1 ring-violet-200'
                        : 'border-outline-variant/40 bg-surface hover:border-violet-300 hover:bg-violet-50/30')
                    }
                  >
                    <div className="relative aspect-video w-full bg-neutral-100">
                      {previewUrl ? (
                        <>
                          <img src={previewUrl} alt={lk.label} className="h-full w-full object-cover" />
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setLightboxUrl(previewUrl); }}
                            className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition group-hover:opacity-100"
                          >
                            <span className="text-[10px] text-white">查看</span>
                          </button>
                        </>
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-neutral-300">
                          <ImageOff className="h-5 w-5" />
                        </div>
                      )}
                      {lockedCount > 0 && (
                        <span className="absolute right-1 top-1 rounded bg-brand-500 px-1 py-0 text-[9px] text-white">
                          {lockedCount}
                        </span>
                      )}
                    </div>
                    <div className="px-2 py-1.5">
                      <p className="truncate text-[11px] font-medium text-neutral-800">{lk.label}</p>
                      {lk.description && (
                        <p className="truncate text-[10px] text-neutral-400">{lk.description}</p>
                      )}
                    </div>
                  </button>

                  {!isBase && active && (
                    <div className="mt-0.5 flex gap-1 px-0.5">
                      <button
                        type="button"
                        onClick={() => {
                          if (isEditingThis) { setEditingLookId(null); }
                          else { setEditingLookId(lk.id); setEditLookDesc(lk.description ?? ''); }
                        }}
                        className="flex-1 rounded py-0.5 text-[10px] text-neutral-400 hover:text-violet-600"
                      >
                        {isEditingThis ? '取消' : <><Pencil className="inline h-2.5 w-2.5" /> 编辑</>}
                      </button>
                      <button
                        type="button"
                        onClick={() => { if (window.confirm('确定删除此形象？')) deleteLookMut.mutate(lk.id); }}
                        className="rounded py-0.5 text-[10px] text-neutral-400 hover:text-rose-600"
                      >
                        <Trash2 className="inline h-2.5 w-2.5" />
                      </button>
                    </div>
                  )}

                  {isEditingThis && active && (
                    <div className="mt-1 space-y-1 rounded border border-violet-200 bg-violet-50/40 p-1.5">
                      <input
                        type="text"
                        value={editLookDesc}
                        onChange={(e) => setEditLookDesc(e.target.value)}
                        maxLength={20}
                        placeholder="形象描述"
                        className="w-full rounded border border-outline-variant/40 bg-white px-1.5 py-0.5 text-[11px] focus:outline-none"
                      />
                      <Button
                        size="sm"
                        variant="agent"
                        agentColor="director"
                        className="w-full"
                        disabled={updateLookMut.isPending}
                        onClick={() => updateLookMut.mutate({ id: lk.id, desc: editLookDesc })}
                      >
                        {updateLookMut.isPending ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Save className="h-2.5 w-2.5" />}
                        保存
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="shrink-0 border-t border-outline-variant/20 p-2">
            {showNewLookForm ? (
              <div className="space-y-1.5">
                <input
                  type="text"
                  value={newLookDesc}
                  onChange={(e) => setNewLookDesc(e.target.value)}
                  maxLength={20}
                  placeholder="形象描述，如：雨天/废墟"
                  className="w-full rounded border border-outline-variant/40 bg-white px-2 py-1 text-[11px] focus:border-violet-400 focus:outline-none"
                />
                <div className="flex gap-1">
                  <Button size="sm" variant="agent" agentColor="director" className="flex-1" disabled={createLookMut.isPending} onClick={() => createLookMut.mutate()}>
                    {createLookMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                    创建
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setShowNewLookForm(false); setNewLookDesc(''); }}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowNewLookForm(true)}
                className="flex w-full items-center justify-center gap-1 rounded-lg border border-dashed border-outline-variant/40 py-2 text-[11px] text-neutral-400 transition hover:border-violet-300 hover:text-violet-600"
              >
                <Plus className="h-3 w-3" /> 新增形象
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4 text-sm">

          <div className="rounded-md border border-violet-100 bg-violet-50/30 px-3 py-2">
            <div className="flex items-center gap-1.5 text-xs font-medium text-violet-700">
              定场图
              {lockedByVariant.establishing && <Check className="h-3 w-3 text-brand-500" />}
            </div>
            <p className="mt-1 text-[11px] leading-relaxed text-violet-500">{VARIANT_DESC.establishing}</p>
          </div>

          <section className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold text-neutral-500">
                补充说明{activeLook && activeLook.label !== '标准形象' && ` · ${activeLook.label}`}
              </span>
              {!notesEditing && (
                <button
                  type="button"
                  onClick={() => { setNotesDraft(sourceNotes); setNotesEditing(true); }}
                  className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-neutral-400 hover:text-brand-600"
                >
                  <Pencil className="h-3 w-3" /> 编辑
                </button>
              )}
            </div>
            {!notesEditing ? (
              <div className="min-h-[2rem] rounded border border-outline-variant/20 bg-surface-variant/20 px-2 py-1.5 text-[11px] leading-relaxed text-neutral-600 whitespace-pre-wrap">
                {sourceNotes || <span className="text-neutral-400">暂无补充说明</span>}
              </div>
            ) : (
              <div className="space-y-1.5">
                <textarea
                  rows={3}
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                  className="w-full resize-none rounded border border-outline-variant/30 bg-white px-2 py-1 text-[11px] leading-relaxed text-neutral-700 placeholder:text-neutral-400 focus:border-brand-300 focus:outline-none"
                  placeholder="为 AI 出图补充场地描述…"
                />
                <div className="flex gap-1.5 rounded-md border border-brand-100 bg-brand-50/40 p-2">
                  <input
                    type="text"
                    value={notesHint}
                    onChange={(e) => setNotesHint(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !generateNotesMut.isPending && generateNotesMut.mutate()}
                    placeholder="AI 生成要求（可选）…"
                    className="min-w-0 flex-1 rounded border border-outline-variant/40 bg-white px-2 py-1 text-[11px] text-neutral-700 placeholder:text-neutral-400 focus:border-brand-300 focus:outline-none"
                  />
                  <Button size="sm" variant="agent" agentColor="director" onClick={() => generateNotesMut.mutate()} disabled={generateNotesMut.isPending}>
                    {generateNotesMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                    AI 生成
                  </Button>
                </div>
                {notesAiPreview !== null && (
                  <div className="space-y-1">
                    <div className="text-[10px] text-brand-600">生成结果（确认后点「使用」）</div>
                    <pre className="max-h-32 overflow-y-auto whitespace-pre-wrap rounded border border-brand-200 bg-white px-2 py-1 text-[11px] leading-relaxed text-neutral-700">{notesAiPreview}</pre>
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="agent" agentColor="director" onClick={() => { setNotesDraft(notesAiPreview); setNotesAiPreview(null); }}>
                        <Check className="h-3 w-3" /> 使用
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setNotesAiPreview(null)}>
                        <X className="h-3 w-3" /> 放弃
                      </Button>
                    </div>
                  </div>
                )}
                <div className="flex justify-end gap-1.5">
                  <Button size="sm" variant="ghost" onClick={() => { setNotesEditing(false); setNotesAiPreview(null); }} disabled={saveNotesMut.isPending}>
                    <X className="h-3 w-3" /> 取消
                  </Button>
                  <Button size="sm" variant="agent" agentColor="director" onClick={() => saveNotesMut.mutate()} disabled={saveNotesMut.isPending || notesDraft === sourceNotes}>
                    {saveNotesMut.isPending ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Save className="h-2.5 w-2.5" />}
                    保存
                  </Button>
                </div>
              </div>
            )}
          </section>

          {availableCharRefs.length > 0 && (
            <section className="space-y-1.5 rounded-md border border-violet-100 bg-violet-50/30 p-3">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-violet-700">
                  人物设定图 · 风格参考（可选）
                </span>
                <span className="text-[10px] text-violet-400">
                  已选 {Object.keys(selectedCharRefs).length}/{availableCharRefs.length}
                </span>
              </div>
              <p className="text-[10px] leading-relaxed text-violet-500">
                选择已生成的人物设定图作为画风/光影/情绪参考，辅助场景出图。
                人物不会出现在场景图中，仅作风格引导。
              </p>
              <div className="flex flex-wrap gap-1.5">
                {availableCharRefs.map((ref) => {
                  const isSelected = ref.id in selectedCharRefs;
                  return (
                    <button
                      key={ref.id}
                      type="button"
                      onClick={() => toggleCharRef(ref.id, ref.url)}
                      title={ref.name}
                      className={
                        'relative flex items-center gap-1.5 rounded-full border px-2 py-1 text-[11px] transition ' +
                        (isSelected
                          ? 'border-violet-400 bg-violet-100 text-violet-700 ring-1 ring-violet-200'
                          : 'border-outline-variant/40 bg-surface text-neutral-500 hover:border-violet-300')
                      }
                    >
                      <div className="flex h-5 w-5 items-center justify-center overflow-hidden rounded-full bg-neutral-100">
                        <img src={ref.url} alt={ref.name} className="h-full w-full object-cover" />
                      </div>
                      <span className="max-w-[100px] truncate">{ref.name}</span>
                      {isSelected && (
                        <X className="h-3 w-3 shrink-0 text-violet-400" />
                      )}
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          <section className="space-y-2 rounded-md border border-outline-variant/30 bg-surface-variant/30 p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] text-neutral-500">分辨率</span>
                  {(['2K', '4K'] as Resolution[]).map((r) => (
                    <button key={r} type="button" onClick={() => setResolution(r)}
                      className={`rounded-full border px-2 py-0.5 text-[11px] transition ${resolution === r ? 'border-brand-400 bg-brand-50 text-brand-700' : 'border-outline-variant/40 bg-surface text-neutral-500 hover:border-brand-300'}`}
                    >{r}</button>
                  ))}
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] text-neutral-500">张数</span>
                  {([1, 2, 3] as Count[]).map((n) => (
                    <button key={n} type="button" onClick={() => setCount(n)}
                      className={`rounded-full border px-2 py-0.5 text-[11px] transition ${count === n ? 'border-brand-400 bg-brand-50 text-brand-700' : 'border-outline-variant/40 bg-surface text-neutral-500 hover:border-brand-300'}`}
                    >{n}张</button>
                  ))}
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] text-neutral-500">模型</span>
                  {(['seedream', 'gpt-image'] as const).map((m) => (
                    <button key={m} type="button" onClick={() => setModel(m)}
                      className={`rounded-full border px-2 py-0.5 text-[11px] transition ${model === m ? 'border-brand-400 bg-brand-50 text-brand-700' : 'border-outline-variant/40 bg-surface text-neutral-500 hover:border-brand-300'}`}
                    >{m === 'seedream' ? 'Seedream' : 'GPT-Image'}</button>
                  ))}
                </div>
              </div>
              <Button variant="agent" agentColor="director" size="sm" onClick={() => generateMut.mutate()} disabled={isGenRunning}>
                {isGenRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                抽 {count} 张候选
              </Button>
            </div>
            {currentPollTaskId && <p className="text-[10px] text-neutral-400">生成中… {taskQuery.data?.status ?? 'queued'}</p>}
          </section>

          <section className="space-y-2">
            <h4 className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
              抽卡历史 · {VARIANT_LABEL[variant]}
            </h4>
            {runsByVariant.length === 0 ? (
              <p className="text-[11px] text-neutral-400">暂无抽卡记录</p>
            ) : (
              <div className="space-y-3">
                {runsByVariant.map((run) => (
                  <RunRow
                    key={run.id}
                    run={run}
                    selectedUrl={currentLocked}
                    onSelect={(candidateId) => selectMut.mutate({ runId: run.runId, candidateId })}
                    onView={(url) => setLightboxUrl(url)}
                    onDelete={() => {
                      if (window.confirm('确定删除这一轮抽卡历史？如果当前锁定图来自该轮，也会一并清除。')) {
                        deleteRunMut.mutate(run.runId);
                      }
                    }}
                    disabled={selectMut.isPending}
                    deleteDisabled={deleteRunMut.isPending}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      <Lightbox url={lightboxUrl} alt="场地图" onClose={() => setLightboxUrl(null)} />
    </Modal>
  );
}

function RunRow({
  run, selectedUrl, onSelect, onView, onDelete, disabled, deleteDisabled,
}: {
  run: LocationLockRun;
  selectedUrl?: string | null;
  onSelect: (candidateId: string) => void;
  onView: (url: string) => void;
  onDelete: () => void;
  disabled?: boolean;
  deleteDisabled?: boolean;
}) {
  const allSettled = run.candidates.length > 0 && run.candidates.every((c) => c.url || c.error);
  const running = !allSettled && (run.status === 'running' || run.status === 'queued');
  const total = Math.max(run.count ?? 0, run.candidates.length, running ? 1 : 0);
  const slots = Array.from({ length: total });

  return (
    <div className="rounded-md border border-outline-variant/20 bg-surface p-2">
      <div className="mb-1.5 flex items-center gap-2 text-[10px] text-neutral-500">
        <span>{run.size} · {run.status}</span>
        {running && (
          <span className="inline-flex items-center gap-1 text-brand-600">
            <Loader2 className="h-3 w-3 animate-spin" /> 生成中…
          </span>
        )}
        <span className="ml-auto">{run.createdAt?.slice(0, 16).replace('T', ' ')}</span>
        <button
          type="button"
          disabled={deleteDisabled}
          onClick={onDelete}
          className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-neutral-400 transition hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
          title="删除这一轮抽卡历史"
        >
          {deleteDisabled ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
          删除
        </button>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {slots.map((_, i) => {
          const c = run.candidates[i];
          if (!c) {
            return (
              <div
                key={`ph-${i}`}
                className="flex aspect-video animate-pulse items-center justify-center rounded border border-dashed border-outline-variant/40 bg-surface-variant/40 text-outline"
              >
                <ImageOff className="h-4 w-4" />
              </div>
            );
          }
          const isSelected = !!c.url && c.url === selectedUrl;
          return (
            <div key={c.id} className={`relative overflow-hidden rounded border transition ${isSelected ? 'border-brand-500 ring-2 ring-brand-300' : 'border-outline-variant/40'} ${!c.url ? 'cursor-not-allowed opacity-60' : ''}`}>
              {c.url ? (
                <>
                  <button
                    type="button"
                    onClick={() => c.url && onView(c.url)}
                    className="block w-full"
                  >
                    <img src={c.url} alt="候选" className="aspect-video w-full object-cover" />
                  </button>
                  {isSelected && (
                    <span className="absolute left-1 top-1 rounded-full bg-brand-500 p-0.5 text-white">
                      <Check className="h-2.5 w-2.5" />
                    </span>
                  )}
                  <button
                    type="button"
                    disabled={disabled || isSelected}
                    onClick={(e) => { e.stopPropagation(); c.url && onSelect(c.id); }}
                    className={`absolute bottom-0 left-0 right-0 py-1 text-center text-[9px] font-medium transition ${isSelected ? 'bg-brand-500 text-white' : 'bg-black/50 text-white opacity-0 hover:opacity-100'}`}
                  >
                    {isSelected ? '已选定' : '选定'}
                  </button>
                </>
              ) : c.error ? (
                <div className="flex aspect-video items-center justify-center bg-red-50 px-1 text-center text-[9px] text-red-600">{c.error}</div>
              ) : (
                <div className="flex aspect-video items-center justify-center bg-neutral-100">
                  <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
